// useEducationConfiguration.ts
import { useCallback, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import epsClient from 'enrollment-platform/api/eps.client'

export type EmployeeHandbookLanguage = 'english' | 'spanish'

export interface EmployeeHandbookDataDto {
  fileName: string
  s3Key: string
  language: EmployeeHandbookLanguage
  year: number
  uploadedAt: string
  uploadedBy?: string
}

export interface BrainSharkUrlDto {
  year: number
  url: string | null
}

interface EducationConfigResponse {
  brainSharkUrls: BrainSharkUrlDto[]
  employeeHandbooks: EmployeeHandbookDataDto[]
  healthplanChecklists?: EmployeeHandbookDataDto[]
}

export default function useEducationConfiguration(customerId: string) {
  const { data, isLoading, refetch } = useQuery<EducationConfigResponse>({
    queryKey: ['education-config', customerId],
    queryFn: async () => {
      if (!customerId) {
        return {
          brainSharkUrls: [],
          employeeHandbooks: [],
          healthplanChecklists: [],
        }
      }

      const res = await epsClient.get<EducationConfigResponse>(
        `/api/v1/employee-experience/customers/${customerId}/education`
      )
      return res.data
    },
  })

  const [isDeleting, setIsDeleting] = useState(false)
  const [isDeletingChecklist, setIsDeletingChecklist] = useState(false)

  const deleteHandbook = useCallback(
    async (hb: EmployeeHandbookDataDto) => {
      setIsDeleting(true)
      try {
        await epsClient.delete(
          `/api/v1/employee-experience/customers/${customerId}/handbook`,
          {
            data: {
              year: hb.year,
              language: hb.language,
            },
          }
        )
        await refetch()
      } finally {
        setIsDeleting(false)
      }
    },
    [customerId, refetch]
  )

  const deleteHealthplanChecklist = useCallback(
    async (doc: EmployeeHandbookDataDto) => {
      setIsDeletingChecklist(true)
      try {
        await epsClient.delete(
          `/api/v1/employee-experience/customers/${customerId}/healthplan-checklist`,
          {
            data: {
              year: doc.year,
              language: doc.language,
            },
          }
        )
        await refetch()
      } finally {
        setIsDeletingChecklist(false)
      }
    },
    [customerId, refetch]
  )

  const employeeHandbooks = data?.employeeHandbooks ?? []
  const healthplanChecklists = data?.healthplanChecklists ?? []
  const brainSharkUrls = data?.brainSharkUrls ?? []

  const getBrainSharkUrlForYear = useCallback(
    (year: number): string => {
      const record = brainSharkUrls.find((b) => b.year === year)
      return record?.url ?? ''
    },
    [brainSharkUrls]
  )

  const availableYears = useMemo(() => {
    const handbookYears = employeeHandbooks.map((hb) => hb.year)
    const checklistYears = healthplanChecklists.map((cl) => cl.year)
    const brainSharkYears = brainSharkUrls.map((b) => b.year)
    const currentYear = new Date().getFullYear()
    const nextYear = currentYear + 1

    const years = Array.from(
      new Set([
        ...handbookYears,
        ...checklistYears,
        ...brainSharkYears,
        currentYear,
        nextYear,
      ])
    )
    return years.sort((a, b) => b - a)
  }, [employeeHandbooks, healthplanChecklists, brainSharkUrls])

  const saveBrainSharkUrl = useCallback(
    async (year: number, url: string) => {
      if (!customerId) return

      await epsClient.post(
        `/api/v1/employee-experience/customers/${customerId}/education/brainshark-url`,
        { year, url }
      )

      await refetch()
    },
    [customerId, refetch]
  )

  return {
    isLoading,
    employeeHandbooks,
    healthplanChecklists,
    brainSharkUrls,
    getBrainSharkUrlForYear,
    availableYears,
    saveBrainSharkUrl,
    refetch,
    deleteHandbook,
    deleteHealthplanChecklist,
    isDeleting,
    isDeletingChecklist,
  }
}
